import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import Badge from "../components/Badge";
import Banner from "../components/Banner";
import Button from "../components/Button";
import Card from "../components/Card";
import ChevronRight from "../components/icons/ChevronRight";
import GhostButton from "../components/GhostButton";
import LinkButton from "../components/LinkButton";
import { RowSpinner } from "../components/Loader/Loader";
import { accountTitle, formatDate, providerName } from "../components/syncunit/format";
import Dropdown from "../components/Dropdown";
import ImageShow from "../components/ImageShow";
import Link from "../components/Link";
import Reports from "../components/Reports";
import { AccountTile } from "../components/UploadedAccount";
import {
  ENABLE_INVESTING_ACCOUNT_CONTROLLER,
  ENABLE_SYNC_UNIT_CONTROLLER,
  ENABLE_UNIFIED_UPLOAD,
  FREEDOM_TYPE,
  INTERACTIVE_TYPE,
} from "../constants/constants";
import { investingAccountPath } from "../constants/syncRoutes";
import useNavigation from "../hooks/use-navigation";
import useToken from "../hooks/use-token";
import { useSynchronizedFilesQuery } from "../store";
import { UNIFIED_UPLOAD_PATH } from "./UnifiedUploadPage";

function LegacyReportsPage() {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const [dataProvider, setDataProvider] = useState("");
  let params = {
    dataProvider: dataProvider,
    page: 0,
    size: 10,
  };
    
  const [mobileScreen, setMobileScreen] = useState(false);

  const { data, error, isFetching, refetch } =
    useSynchronizedFilesQuery(params);

  const [selection, setSelection] = useState(null);

  const unifiedEntry = ENABLE_UNIFIED_UPLOAD && ENABLE_INVESTING_ACCOUNT_CONTROLLER;
  const noReports =
    !isFetching && !error && dataProvider === "" && Array.isArray(data?.content) && data.content.length === 0;
  useEffect(() => {
    if (unifiedEntry && noReports) {
      navigate(UNIFIED_UPLOAD_PATH, { replace: true });
    }
  }, [unifiedEntry, noReports, navigate]);

  const handleSelect = (option) => {
    setSelection(option);
    if (option.value === "freedom") {
      setDataProvider(FREEDOM_TYPE);
      params.dataProvider = dataProvider;
    } else if (option.value === "interactive") {
      setDataProvider(INTERACTIVE_TYPE);
      params.dataProvider = dataProvider;
    } else {
      setDataProvider("");
      params.dataProvider = dataProvider;
    }
    refetch(params);
  };

  const options = [
    { label: t('reports.broker'), value: "broker" },
    { label: "Freedom Broker", value: "freedom" },
    { label: "InteractiveBrokers", value: "interactive" },
  ];

  const content = <Reports data={data?.content} />;

  useEffect(() => {
    const handleResize = () => {
      setMobileScreen(window.innerWidth < 460);
    };
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if(mobileScreen) {
    return (
      <div className="page">
        <h1 className="text-black inline-block text-lg leading-10 sm:leading-normal sm:text-2xl sm:pb-1">
        {t('reports.title')}
        </h1>

        <div
            className="w-full items-center py-4 mt-4 sm:mt-16 min-h-[80px] 
          flex justify-left px-5 sm:px-7 border-transparent rounded-xl text-white 
          bg-gradient-to-r from-cyan-650 to-cyan-75">
            <p className="text-sm sm:text-normal">
              {t('reports.mobileWarning')}
            </p>
          </div>
      </div>
    )
  }

  return (
    <div className="page flex flex-col">
      <h1 className="text-black text-lg sm:text-2xl sm:pb-3">{t('reports.title')}</h1>
      <div className="py-5 flex sm:mb-20">
        <form className="relative">
          <input
            className="pl-8 rounded-md text-sm text-gray-350 border border-gray-150 w-40 h-10 focus-visible:outline-cyan-450"
            type="text"
          />
          <Button className="absolute top-[10px] left-2">
            <ImageShow
              className=""
              image={{ url: "search", label: "Search" }}
            />
          </Button>
        </form>
        <div className="flex ml-2.5">
          <Dropdown options={options} value={selection} onChange={handleSelect}>
            <span className="text-sm text-gray-350 pr-2">{t('reports.broker')}</span>
          </Dropdown>
        </div>
        <Link
          to={unifiedEntry ? UNIFIED_UPLOAD_PATH : "/accounts"}
          className="hidden sm:flex ml-auto h-[37px] items-center justify-center border-transparent rounded-xl text-white bg-gradient-to-r from-cyan-650 to-cyan-75 hover:from-cyan-900 hover:to-cyan-650 px-3 text-sm">
          {t('reports.importReports')}
        </Link>
      </div>
      <div className="[&>*:not(:first-child)]:border-t [&>*:last-child]:border-b">
        {content && content}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <div className="text-xs text-gray-350 justify-self-start">
          {data && data?.size + "/" + data?.totalElements}
        </div>
        <div className="flex text-gray-350 mx-auto">
          <Link
            className="pointer-events-none"
            to="/"
            onClick={(e) => e.preventDefault()}>
            <ImageShow
              className=""
              image={{ url: "arrow-left", label: "Arrow Left" }}
            />
          </Link>
          <Link className="pt-px" to="/" onClick={(e) => e.preventDefault()}>
            1
          </Link>
          <Link
            className="pointer-events-none"
            to="/"
            onClick={(e) => e.preventDefault()}>
            <ImageShow
              className="ml-2"
              image={{ url: "arrow-right", label: "Arrow Right" }}
            />
          </Link>
        </div>
      </div>
      <div className="flex mt-4">
        <Link 
          to="/taxreports"
          className="sm:flex h-14 w-52 items-center justify-center border-transparent rounded-xl text-white bg-gradient-to-r from-cyan-650 to-cyan-75 hover:from-cyan-900 hover:to-cyan-650 px-3 text-sm"
        >
          {t('reports.calculate')}
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// broker-report-sync SCR-04 «Мої рахунки»: every provider in one list, 50 per page, «Показати ще» (AC-16).

const MY_ACCOUNTS_PAGE_SIZE = 50;

function CenteredCard({ children }) {
  return (
    <Card data-testid="centered-card" className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-sm text-text-muted">
      {children}
    </Card>
  );
}

function MyAccountRow({ account }) {
  const { t } = useTranslation();
  return (
    <li data-testid="my-account" className="list-none">
      <Card
        variant="interactive"
        to={investingAccountPath(account.id)}
        data-testid="account-surface"
        className="flex items-center gap-4 px-5 py-4"
      >
        <AccountTile />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium break-all min-w-0">{accountTitle(account)}</span>
            <Badge tone="accent">{providerName(account.dataProvider)}</Badge>
            {account.hasOpenSyncUnit && <Badge tone="muted">{t("reports.myAccounts.openSync")}</Badge>}
          </div>
          <p className="text-xs text-text-muted mt-0.5">
            {t("reports.myAccounts.lastSync", {
              date: formatDate(account.lastSynchronizationDateTime),
              count: account.transactionCount ?? 0,
            })}
          </p>
        </div>
        <ChevronRight />
      </Card>
    </li>
  );
}

function MyAccountsPage() {
  const { t } = useTranslation();
  const token = useToken();
  const [page, setPage] = useState(0);
  const [loaded, setLoaded] = useState([]);
  const { currentData, error, isFetching, refetch } = useSynchronizedFilesQuery(
    { page, size: MY_ACCOUNTS_PAGE_SIZE },
    { skip: !token, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (!currentData) return;
    setLoaded((previous) => {
      const content = currentData.content || [];
      const kept = page === 0 ? [] : previous.filter((row) => !content.some((item) => item.id === row.id));
      return [...kept, ...content];
    });
  }, [currentData, page]);

  // A new account with only its first open synchronization has no confirmed chronology yet — not shown here.
  const accounts = useMemo(() => loaded.filter((account) => account.lastSynchronizationDateTime), [loaded]);
  const lastPage = currentData ? (currentData.number ?? page) + 1 >= (currentData.totalPages ?? 1) : true;

  let content;
  if (error && loaded.length === 0) {
    content = (
      <Banner tone="danger">
        <p>{t("reports.myAccounts.error")}</p>
        <LinkButton onClick={refetch}>{t("reports.myAccounts.retry")}</LinkButton>
      </Banner>
    );
  } else if (loaded.length === 0 && (isFetching || !currentData)) {
    content = (
      <CenteredCard>
        <RowSpinner />
        {t("reports.myAccounts.loading")}
      </CenteredCard>
    );
  } else if (accounts.length === 0 && lastPage) {
    content = <CenteredCard>{t("reports.myAccounts.empty")}</CenteredCard>;
  } else {
    content = (
      <>
        <ul className="flex flex-col gap-3">
          {accounts.map((account) => (
            <MyAccountRow key={account.id} account={account} />
          ))}
        </ul>
        {error && <Banner tone="danger">{t("reports.myAccounts.error")}</Banner>}
        {!lastPage && (
          <GhostButton className="self-start" disabled={isFetching} aria-busy={isFetching || undefined} onClick={() => setPage(page + 1)}>
            {t("reports.myAccounts.more")}
          </GhostButton>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-5 md:px-10 py-8 max-w-[1040px]" data-testid="my-accounts-page">
      <h1 className="text-2xl font-semibold tracking-tight">{t("reports.myAccounts.title")}</h1>
      <p className="text-sm text-text-muted">{t("reports.myAccounts.explanation")}</p>
      {content}
    </div>
  );
}

// SCR-04: with ENABLE_SYNC_UNIT_CONTROLLER — «Мої рахунки»; without — the existing /reports as is.
function ReportsPage() {
  return ENABLE_SYNC_UNIT_CONTROLLER ? <MyAccountsPage /> : <LegacyReportsPage />;
}

export default ReportsPage;
